<template>
  <div class="w-full py-16 bg-vp-base-100 ">
    <div class="mx-auto w-full sm:w-10/12 md:w-8/12 lg:w-4/12 xl:3/12 2xl:w-2/12">
      <h1 class="text-2xl my-8 text-center">
        {{ $gettext('Log In') }}
      </h1>

      <Form
        :form-id="formId"
        class="vp-login-form p-8"
        @submit="onSubmit"
      >
        <VPInput
          input-id="email"
          :label="$gettext('Email')"
          :placeholder="$gettext('Enter your email address')"
          autocomplete="username"
          autocapitalize="off"
          autocorrect="off"
          :disabled="loading"
        />
        <VPInput
          input-id="password"
          :label="$gettext('Password')"
          :placeholder="$gettext('Enter your password')"
          type="password"
          input-element-type="password"
          autocomplete="current-password"
          :disabled="loading"
        />

        <VPButton
          type="submit"
          class="my-4 block w-full disabled:cursor-not-allowed"
          :disabled="loading"
        >
          {{ $gettext('Log In') }}
        </VPButton>

        <a
          class="float-right"
          href="/forgot-password"
        >{{ $gettext("Forgot your password") }}</a>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Auth } from '@vp/common';
import { useRootStore } from '@/stores/root';
import VPButton from '@/components/form/VPButton.vue';
import VPInput from '@/components/form/VPInput.vue';
import { Form, useFormsStoreComposable } from 'vuniform';
import { useConnectionStore } from '@/stores/connection';
import { ref } from 'vue';

const rootStore = useRootStore();
const connectionStore = useConnectionStore();
const loading = ref<boolean>(false);

const { formId, formGetValues, formsStore } = useFormsStoreComposable('login-form');

const onSubmit = async () => {
  const { cognito } = rootStore.config!;

  if (!cognito || !cognito.clientId || !cognito.region) {
    throw new Error('Cognito config is missing');
  }

  const { email, password } = formGetValues<{ email: string; password: string }>();

  try {
    loading.value = true;
    formsStore.CLEAR_ERRORS(formId);
    const { AccessToken, IdToken } = await Auth.getCognitoToken(email, password, {
      clientId: cognito.clientId,
      region: cognito.region,
    });

    const { authToken, returnTo, errors } = await Auth.exchangeCognitoToken(connectionStore.apiClient!, AccessToken!, IdToken);
    if (errors?.length) {
      formsStore.SET_ERRORS({
        requesterId: formId,
        errors: {
          password: errors.map(e => e.message),
        },
      });
      return;
    }

    connectionStore.setAuthToken(authToken);

    if (returnTo) {
      window.location.replace(returnTo);
    } else {
      window.location.replace('/');
    }
  } catch (e) {
    formsStore.SET_ERRORS({
      requesterId: formId,
      errors: {
        password: [e.message],
      },
    });
  } finally {
    loading.value = false;
  }
};
</script>
